import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useStrings } from '../../i18n';
import { auth, googleProvider } from '../../lib/firebase';
import { signInWithPopup, signInWithRedirect, signOut } from 'firebase/auth';

export default function AuthButton({ isMobileCompact = false, redirectTo = null }) {
  const navigate = useNavigate();
  const { userInfo, isAuthenticated } = useAuth();
  const strings = useStrings();
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const handleLogin = async () => {
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (error) {
      // popup-blocked: fall back to redirect (common on mobile and some browsers in prod)
      if (
        error.code === 'auth/popup-blocked' ||
        error.code === 'auth/popup-closed-by-user' ||
        error.code === 'auth/cancelled-popup-request'
      ) {
        try {
          await signInWithRedirect(auth, googleProvider);
        } catch (redirectError) {
          console.error('Login redirect error:', redirectError);
        }
      } else {
        console.error('Login error:', error);
      }
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setDropdownOpen(false);
      // Always navigate to home on logout — clears any protected-page context
      navigate('/');
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  // Mobile compact version - show user avatar/initial with dropdown
  if (isMobileCompact && isAuthenticated) {
    const userName = userInfo?.displayName?.split(" ")[0] || strings.auth.anonymous;
    const userInitial = userName.charAt(0).toUpperCase();

    return (
      <div className="relative">
        {/* User Avatar Button */}
        <button
          onClick={() => setDropdownOpen(!dropdownOpen)}
          className="w-10 h-10 rounded-full bg-secondary text-surface font-bold text-sm hover:opacity-90 transition flex items-center justify-center shadow-sm focus:outline-none"
          title={userName}
          aria-expanded={dropdownOpen}
        >
          {userInitial}
        </button>

        {/* Dropdown Menu */}
        {dropdownOpen && (
          <div className="absolute right-0 mt-3 w-56 bg-surface border-2 border-secondary rounded-xl shadow-lg z-50 overflow-hidden">
            {/* Header Section */}
            <div className="px-4 py-4 bg-gradient-to-br from-primaryBg to-cardBg border-b-2 border-secondary">
              <p className="text-sm font-bold text-headingText truncate">{userName}</p>
              <p className="text-xs text-bodyText truncate mt-1">{userInfo?.email}</p>
            </div>
            
            {/* Menu Items */}
            <button
              onClick={() => {
                navigate('/profile');
                setDropdownOpen(false);
              }}
              className="w-full text-left px-4 py-3 text-sm font-medium text-bodyText hover:bg-primaryBg transition flex items-center justify-between group"
            >
              <span>👤 My Profile</span>
            </button>

            <button
              onClick={() => {
                navigate('/orders');
                setDropdownOpen(false);
              }}
              className="w-full text-left px-4 py-3 text-sm font-medium text-bodyText hover:bg-primaryBg transition flex items-center justify-between group"
            >
              <span>📦 My Orders</span>
            </button>

            <button
              onClick={() => {
                navigate('/profile/edit');
                setDropdownOpen(false);
              }}
              className="w-full text-left px-4 py-3 text-sm font-medium text-bodyText hover:bg-primaryBg transition flex items-center justify-between group"
            >
              <span>⚙️ Edit Profile</span>
            </button>

            {/* Divider */}
            <div className="h-px bg-gray-300"></div>
            
            {/* Logout Button */}
            <button
              onClick={handleLogout}
              className="w-full text-left px-4 py-3 text-sm font-medium text-surface bg-secondary hover:bg-opacity-90 transition flex items-center justify-between group"
            >
              <span>{strings.auth.logout}</span>
              <svg className="w-4 h-4 opacity-0 group-hover:opacity-100 transition" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </button>
          </div>
        )}

        {/* Close dropdown when clicking outside */}
        {dropdownOpen && (
          <div
            className="fixed inset-0 z-40"
            onClick={() => setDropdownOpen(false)}
          />
        )}
      </div>
    );
  }

  // Mobile compact version - login button (icon style)
  if (isMobileCompact && !isAuthenticated) {
    return (
      <button
        onClick={handleLogin}
        className="px-3 py-2 text-sm font-medium bg-secondary text-surface rounded-lg hover:opacity-90 transition whitespace-nowrap shadow-sm"
      >
        {strings.auth.login}
      </button>
    );
  }

  // Desktop version
  return isAuthenticated ? (
    <div className="flex items-center space-x-3">
      <span className="text-sm font-medium text-bodyText">
        {strings.auth.greeting},{" "}
        {userInfo?.displayName?.split(" ")[0] || strings.auth.anonymous}
      </span>
      <div className="relative">
        {/* User Avatar/Dropdown Button */}
        <button
          onClick={() => setDropdownOpen(!dropdownOpen)}
          className="px-3 py-2 bg-secondary text-surface rounded-lg hover:opacity-90 transition font-medium text-sm flex items-center gap-2"
        >
          <span>▼</span>
        </button>

        {/* Dropdown Menu */}
        {dropdownOpen && (
          <div className="absolute right-0 mt-2 w-48 bg-surface border-2 border-secondary rounded-xl shadow-lg z-50 overflow-hidden">
            {/* Menu Items */}
            <button
              onClick={() => {
                navigate('/profile');
                setDropdownOpen(false);
              }}
              className="w-full text-left px-4 py-3 text-sm font-medium text-bodyText hover:bg-primaryBg transition"
            >
              👤 My Profile
            </button>

            <button
              onClick={() => {
                navigate('/orders');
                setDropdownOpen(false);
              }}
              className="w-full text-left px-4 py-3 text-sm font-medium text-bodyText hover:bg-primaryBg transition"
            >
              📦 My Orders
            </button>

            <button
              onClick={() => {
                navigate('/profile/edit');
                setDropdownOpen(false);
              }}
              className="w-full text-left px-4 py-3 text-sm font-medium text-bodyText hover:bg-primaryBg transition"
            >
              ⚙️ Edit Profile
            </button>

            {/* Divider */}
            <div className="h-px bg-gray-300"></div>
            
            {/* Logout Button */}
            <button
              onClick={handleLogout}
              className="w-full text-left px-4 py-3 text-sm font-medium text-surface bg-secondary hover:bg-opacity-90 transition"
            >
              {strings.auth.logout}
            </button>
          </div>
        )}

        {/* Close dropdown when clicking outside */}
        {dropdownOpen && (
          <div
            className="fixed inset-0 z-40"
            onClick={() => setDropdownOpen(false)}
          />
        )}
      </div>
    </div>
  ) : (
    <button
      onClick={handleLogin}
      className="px-4 py-2 bg-secondary text-surface rounded-lg hover:opacity-90 transition font-medium"
    >
      {strings.auth.login}
    </button>
  );
}
